import { useRef, type KeyboardEvent, type PointerEvent, type RefObject } from "react";

import {
  applyLipSize,
  LIP_MIN,
  LIP_STEP,
  lipMax,
  lipSizeNow,
  releasedLipSize,
  setLipSize,
  useLipSize,
} from "./lipSize";
import { setLipOpen } from "./lipStore";

/** A pointer move shorter than this is a click, not a drag. */
const DRAG_SLOP = 3;
/** After a drag closed the lip: its travel to peek is over, the chosen height goes back. */
const CLOSE_SETTLE_MS = 400;

interface LipDragStart {
  pointerY: number;
  /** The lip's visible height when the drag began: its height open, the bar or hover row shut. */
  height: number;
  moved: boolean;
}

/**
 * The grab handle on the lip's top edge (desktop): drag to resize the open lip, or up from the
 * bar to open it at that height; a drag below the minimum closes it. Double-click goes back to
 * the default height. Keyboard: Up/Down resize, Home/End go to the smallest and largest, Enter
 * opens or closes. A drag only writes the height variable (no render per move) and sets
 * `data-dragging` on the lip, which holds it in place with no transition until release.
 */
export function LipHandle({ lip, open }: { lip: RefObject<HTMLElement>; open: boolean }) {
  const drag = useRef<LipDragStart | null>(null);
  const { now, max } = useLipSize();

  const heightAt = (start: LipDragStart, pointerY: number) =>
    Math.max(0, Math.min(start.height + start.pointerY - pointerY, lipMax(window.innerHeight)));

  const endDrag = () => {
    const start = drag.current;
    drag.current = null;
    if (lip.current) delete lip.current.dataset.dragging;
    return start?.moved ? start : null;
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || !lip.current) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const top = lip.current.getBoundingClientRect().top;
    drag.current = { pointerY: event.clientY, height: window.innerHeight - top, moved: false };
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const start = drag.current;
    if (!start || !lip.current) return;
    if (!start.moved && Math.abs(event.clientY - start.pointerY) < DRAG_SLOP) return;
    start.moved = true;
    lip.current.dataset.dragging = "";
    applyLipSize(heightAt(start, event.clientY));
  };

  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const start = endDrag();
    if (!start) return;
    const height = releasedLipSize(heightAt(start, event.clientY), window.innerHeight);
    if (height == null) {
      setLipOpen(false);
      window.setTimeout(() => applyLipSize(), CLOSE_SETTLE_MS);
      return;
    }
    setLipSize(height);
    setLipOpen(true);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      setLipOpen(!open);
      return;
    }
    const size = lipSizeNow();
    const next = {
      ArrowUp: size + LIP_STEP,
      ArrowDown: size - LIP_STEP,
      Home: LIP_MIN,
      End: lipMax(window.innerHeight),
    }[event.key];
    if (next == null) return;
    event.preventDefault();
    setLipSize(next);
  };

  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      aria-label="Resize Steddy Assistant"
      aria-valuemin={LIP_MIN}
      aria-valuemax={max}
      aria-valuenow={now}
      tabIndex={0}
      data-lip-handle=""
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => {
        if (endDrag()) applyLipSize();
      }}
      onDoubleClick={() => setLipSize(null)}
      onKeyDown={onKeyDown}
      className="group/handle absolute left-1/2 top-0 z-20 hidden h-3 w-16 -translate-x-1/2 cursor-row-resize touch-none items-start justify-center rounded-md pt-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:[@media(hover:hover)_and_(pointer:fine)]:flex"
    >
      <span className="h-1 w-9 rounded-full bg-border transition-colors duration-fade group-hover/handle:bg-muted-foreground/50 group-focus-visible/handle:bg-primary" />
    </div>
  );
}
